import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, imgUrl, money, SWATCH } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronDown, Truck, RotateCcw, ShieldCheck, Sparkles } from "lucide-react";
import { ProductReviews } from "../components/ProductReviews";

const CATEGORY_TITLE = {
  "rings": "Rings",
  "ring-bands": "Ring Bands",
  "bracelets": "Bracelets",
  "necklaces": "Necklaces",
  "earrings": "Earrings",
};

function Accordion({ title, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-[#EFE7D8]" data-testid={testId}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-5 label-track text-[#0A0A0A] hover:text-[#C8A165] transition-colors"
      >
        <span>{title}</span>
        <ChevronDown size={16} strokeWidth={1.4} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden"
          >
            <div className="pb-6 pt-1 text-sm text-[#525252] leading-relaxed">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [color, setColor] = useState(null);
  const [metal, setMetal] = useState("silver");
  const [carat, setCarat] = useState(1);
  const [ringSize, setRingSize] = useState(7);
  const [bandWidth, setBandWidth] = useState(2.0);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      // prefer Gold when available for a consistent luxurious default across the site
      const cols = (data.variants || []).map((v) => v.color);
      const preferred = ["Gold", "18K Yellow Gold", "Yellow Gold", "14K Yellow Gold", "Rose Gold", "White Gold", "Silver"];
      setColor(
        (data.default_color && cols.includes(data.default_color) ? data.default_color : null) ||
        preferred.find((c) => cols.includes(c)) ||
        cols[0]
      );
      setActiveImg(0);
    }).catch(() => setProduct(false));
  }, [id]);

  // Pricing refetch when ring_size/band_width changes (so matrix reflects the true unit cost)
  useEffect(() => {
    if (!id) return;
    const params = { ring_size: ringSize, band_width: bandWidth };
    api.get(`/products/${id}/pricing`, { params }).then(({ data }) => setPricing(data)).catch(() => {});
  }, [id, ringSize, bandWidth]);

  const isRing = product?.category === "rings" || product?.category === "ring-bands";
  const isEarring = product?.category === "earrings";

  if (product === null) return <div className="pt-40 text-center text-[#737373]">Loading…</div>;
  if (product === false) return (
    <div className="pt-40 text-center">
      <p className="font-heading text-3xl text-[#0A0A0A]">Piece not found.</p>
      <Link to="/shop" className="label-track text-[#0A0A0A] mt-6 inline-block">Back to collection</Link>
    </div>
  );

  const variant = product.variants?.find((v) => v.color === color) || product.variants?.[0];
  const images = variant?.images || [];
  const mainImg = images[activeImg] || images[0];
  const price = pricing?.matrix?.[metal]?.[carat];
  const metals = pricing?.metals || [];
  const metalLabel = metals.find((m) => m.id === metal)?.label || metal;
  const ringSizes = pricing?.ring_sizes || [];
  const bandWidths = pricing?.band_widths_mm || [];

  return (
    <div data-testid="product-detail-page" className="pt-10 max-w-[1400px] mx-auto px-6 sm:px-12 min-h-screen pb-20">
      <Link to={`/shop/${product.category}`} className="label-track text-[#737373] hover:text-[#0A0A0A] inline-flex items-center gap-1 mb-8">
        <ChevronLeft size={14} /> {product.category_label || CATEGORY_TITLE[product.category] || product.category}
      </Link>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* GALLERY */}
        <div>
          <motion.div
            key={mainImg}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-[#FFFFFF] aspect-square overflow-hidden relative"
            data-testid="product-main-image"
          >
            {mainImg ? (
              <img src={imgUrl(mainImg)} alt={product.name} className="w-full h-full object-contain p-6" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#737373]">No image</div>
            )}
          </motion.div>

          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-3 mt-3">
              {images.slice(0, 15).map((img, i) => (
                <button
                  key={img}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden bg-[#FFFFFF] transition-opacity ${
                    activeImg === i ? "ring-1 ring-[#0A0A0A]" : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={imgUrl(img)} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="lg:pt-6">
          <p className="label-track text-[#A67C3D] mb-4">{product.category_label || CATEGORY_TITLE[product.category] || product.category}</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-[#0A0A0A] leading-tight">{product.name}</h1>
          <motion.p
            key={`${metal}-${carat}-${ringSize}-${bandWidth}-${price}`}
            initial={{ opacity: 0.4, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            data-testid="product-price"
            className="text-2xl text-[#0A0A0A] mt-5 tracking-tight"
          >
            {price != null ? money(price) : money(product.price)}
          </motion.p>
          <p className="text-xs text-[#737373] mt-1 font-light">
            {carat} ct {pricing?.shape || ""} lab grown diamond · {metalLabel}
            {isRing && ` · Size ${ringSize}${bandWidth ? ` · ${bandWidth}mm` : ""}`}
          </p>

          <div className="h-px bg-[#E5E5E5] my-8" />

          {/* METAL */}
          <div>
            <p className="label-track text-[#737373] mb-3">Metal. <span className="text-[#0A0A0A]">{metalLabel}</span></p>
            <div className="flex flex-wrap gap-3">
              {metals.map((m) => (
                <button key={m.id} data-testid={`metal-${m.id}-selector`} onClick={() => setMetal(m.id)}
                  className={`px-5 py-2.5 label-track text-[11px] border transition-colors ${metal === m.id ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* CARAT */}
          <div className="mt-7">
            <p className="label-track text-[#737373] mb-3">Carat. <span className="text-[#0A0A0A]">{carat}.00 ct</span></p>
            <div className="flex gap-3">
              {(pricing?.carats || [1, 2, 3, 4, 5]).map((c) => (
                <button key={c} data-testid={`carat-${c}-selector`} onClick={() => setCarat(c)}
                  className={`w-12 h-12 font-mono text-sm border transition-colors ${carat === c ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"}`}>
                  {c}ct
                </button>
              ))}
            </div>
          </div>

          {/* RING-ONLY: Size + Band Width */}
          {isRing && ringSizes.length > 0 && (
            <div className="mt-7 grid grid-cols-2 gap-4" data-testid="ring-config">
              <div>
                <label className="label-track text-[#737373] block mb-3">Ring Size <span className="text-[#B76E79]">*</span></label>
                <div className="relative">
                  <select
                    data-testid="ring-size-selector"
                    value={ringSize}
                    onChange={(e) => setRingSize(parseFloat(e.target.value))}
                    required
                    className="w-full appearance-none bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] px-4 py-3 pr-9 font-mono text-sm outline-none transition-colors"
                  >
                    {ringSizes.map((s) => (
                      <option key={s} value={s}>US {s.toFixed(1)}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="label-track text-[#737373] block mb-3">Band Width</label>
                <div className="relative">
                  <select
                    data-testid="band-width-selector"
                    value={bandWidth}
                    onChange={(e) => setBandWidth(parseFloat(e.target.value))}
                    className="w-full appearance-none bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] px-4 py-3 pr-9 font-mono text-sm outline-none transition-colors"
                  >
                    {bandWidths.map((w) => (
                      <option key={w} value={w}>{w.toFixed(2)}mm</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
                </div>
              </div>
            </div>
          )}

          {/* FINISH */}
          <div className="mt-7">
            <p className="label-track text-[#737373] mb-3">Finish. <span className="text-[#0A0A0A]">{color}</span></p>
            <div className="flex items-center gap-5">
              {product.variants?.map((v) => (
                <button key={v.color} data-testid={`variant-${v.color.replace(/\s/g, "")}-selector`}
                  onClick={() => { setColor(v.color); setActiveImg(0); }} title={v.color}
                  className={`w-9 h-9 rounded-full transition-all ${color === v.color ? "ring-1 ring-offset-4 ring-offset-[#FFFFFF] ring-[#A67C3D]" : "hover:scale-105"}`}
                  style={{ backgroundColor: SWATCH[v.color] || "#888" }} />
              ))}
            </div>
          </div>

          <div className="mt-9">
            <button
              data-testid="add-to-cart-button"
              onClick={() => {
                addItem(product, {
                  color, metal, metalLabel, carat,
                  ringSize: isRing ? ringSize : null,
                  bandWidth: isRing ? bandWidth : null,
                  shape: pricing?.shape || "Round",
                  price: price != null ? price : product.price,
                  image: mainImg,
                });
                toast(`${product.name}. ${metalLabel}, ${carat}ct${isRing ? `, Size ${ringSize}` : ""} added.`, { description: "View your selection in the cart." });
              }}
              className="w-full sm:w-auto bg-[#0A0A0A] text-white px-12 py-4 label-track transition-all duration-300 hover:bg-white hover:text-[#0A0A0A] hover:shadow-[inset_0_0_0_1px_#0A0A0A]"
            >
              Add to Selection
            </button>
          </div>

          {/* Trust strip */}
          <div className="mt-8 grid grid-cols-3 gap-4 pb-4">
            <div className="flex items-start gap-2 text-xs text-[#525252]">
              <Truck size={16} strokeWidth={1.4} className="text-[#C8A165] mt-0.5 shrink-0" />
              <span><span className="text-[#0A0A0A]">3-week</span> handcraft + shipping worldwide</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#525252]">
              <RotateCcw size={16} strokeWidth={1.4} className="text-[#C8A165] mt-0.5 shrink-0" />
              <span><span className="text-[#0A0A0A]">30-day</span> easy returns for unworn pieces</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#525252]">
              <ShieldCheck size={16} strokeWidth={1.4} className="text-[#C8A165] mt-0.5 shrink-0" />
              <span><span className="text-[#0A0A0A]">Lifetime</span> quality guarantee</span>
            </div>
          </div>

          {/* Details Accordion */}
          <div className="mt-6 border-b border-[#EFE7D8]">
            <Accordion title="Description" defaultOpen testId="acc-description">
              <p className="max-w-md">{product.description || "A luxurious handset piece from the Dreamonds atelier."}</p>
            </Accordion>
            <Accordion title="Specifications" testId="acc-specs">
              <table className="w-full max-w-md">
                <tbody className="[&>tr>th]:font-normal [&>tr>th]:text-[#737373] [&>tr>th]:text-left [&>tr>th]:py-1.5 [&>tr>th]:pr-6 [&>tr>td]:py-1.5 [&>tr>td]:text-[#0A0A0A] [&>tr>td]:font-mono">
                  <tr><th>Stone shape</th><td>{pricing?.shape || "—"}</td></tr>
                  <tr><th>Carat</th><td>{carat}.00 ct</td></tr>
                  <tr><th>Metal</th><td>{metalLabel}</td></tr>
                  <tr><th>Color / Finish</th><td>{color}</td></tr>
                  {isRing && <tr><th>Ring size (US)</th><td>{ringSize.toFixed(1)}</td></tr>}
                  {isRing && <tr><th>Band width</th><td>{bandWidth.toFixed(2)} mm</td></tr>}
                  <tr><th>Setting</th><td>Handcrafted, atelier finish</td></tr>
                  <tr><th>Certificate</th><td>IGI · issued after purchase</td></tr>
                </tbody>
              </table>
            </Accordion>
            <Accordion title="Shipping &amp; Returns" testId="acc-shipping">
              <ul className="space-y-2 max-w-md">
                <li><span className="text-[#0A0A0A]">Handcraft &amp; shipping:</span> 3 weeks total (Canada &amp; International), fully insured white-glove delivery.</li>
                <li><span className="text-[#0A0A0A]">Returns:</span> 30-day easy returns for unworn pieces. no restocking fee.</li>
                <li><span className="text-[#0A0A0A]">Warranty:</span> Lifetime quality guarantee on every piece.</li>
                <li><Link to="/policies/shipping-returns" className="text-[#C8A165] hover:underline">Read the full Shipping &amp; Returns policy →</Link></li>
              </ul>
            </Accordion>
            <Accordion title="Craftsmanship" testId="acc-craft">
              <p className="max-w-md">
                Every Dreamonds piece is handcrafted in our Canadian atelier from ethically grown,
                IGI certified lab diamonds. From wax sketch to final polish, each stage is completed by
                a single master jeweler. a signature of authorship etched into the inside band.
              </p>
            </Accordion>
          </div>

          <div className="mt-8 flex items-center gap-2 text-[10px] text-[#737373] label-track">
            <Sparkles size={12} className="text-[#C8A165]" />
            <span>{images.length} views in {color}</span>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <ProductReviews productId={product.id} />
    </div>
  );
}
